import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import {
  Article,
  ArticlesIndexParams,
  ArticlesIndexResponse,
  ArticlesSearchParams,
  ArticlesSearchResponse,
  ArticlesShowParams,
  articlesIndexQuery,
  articlesSearchQuery,
  articlesShowQuery,
} from "sdk/articles";
import {
  Author as AuthorsShowResponse,
  AuthorsShowParams,
  authorsShowQuery,
} from "sdk/authors";
import {
  BillingPortalCreateResponse,
  billingPortalCreateQuery,
} from "sdk/billingPortal";
import {
  CancellationReasonsCreateParams,
  cancellationReasonsCreateQuery,
} from "sdk/cancellationReasons";
import {
  DiagnosticCheckoutSessionCreateParams,
  DiagnosticCheckoutSessionCreateResponse,
  diagnosticCheckoutSessionsCreateQuery,
} from "sdk/checkoutSessions";
import { cohortIndexQuery, CohortIndexResponse } from "sdk/cohort";
import {
  CreateConsultationRequest,
  CreateConsultationResponse,
  consultationsCreateQuery,
} from "sdk/consultations";
import {
  CurrentUserIndexResponse,
  CurrentUserUpdateResponse,
  currentUserIndexQuery,
  CurrentUserUpdateParams,
  currentUserUpdateQuery,
} from "sdk/currentUser";
import {
  DelayedPasswordCreateParams,
  delayedPasswordCreateQuery,
} from "sdk/delayedPassword";
import {
  DiagnosticUserIndexResponse,
  diagnosticUserIndexQuery,
} from "sdk/diagnosticUser";
import {
  DiagnosticUserProductsIndexResponse,
  DiagnosticUserProductShowResponse,
  diagnosticUserProductsIndexQuery,
  diagnosticUserProductShowQuery,
} from "sdk/diagnosticUserProducts";
import {
  FeaturesShowParams,
  FeaturesShowResponse,
  featuresShowQuery,
} from "sdk/features";
import {
  Gathering,
  gatheringsIndexQuery,
  GatheringsIndexResponse,
  GatheringsShowParams,
  gatheringsShowQuery,
  gatheringsRsvpQuery,
  gatheringsDeclineQuery,
  GatheringsDeclineParams,
  GatheringsRsvpParams,
} from "sdk/gatherings";
import {
  FacilitiesSearchResponse,
  FacilitiesSearchParams,
  facilitiesSearchQuery,
} from "sdk/labTests";
import {
  MedicalExpert as MedicalExpertsShowResponse,
  MedicalExpertsShowParams,
  medicalExpertsShowQuery,
} from "sdk/medicalExperts";
import {
  NewsletterRegistrationsCreateParams,
  newsletterRegistrationsCreateQuery,
} from "sdk/newsletterRegistrations";
import {
  onboardingIndexQuery,
  OnboardingIndexResponse,
  OnboardingUpdateParams,
  onboardingUpdateQuery,
  OnboardingUpdateResponse,
} from "sdk/onboardings";
import {
  ParticipationsCreateParams,
  participationsCreateQuery,
} from "sdk/participations";
import {
  partnerIndexQuery,
  PartnerIndexResponse,
  PartnerIndexParams,
  partnerBySlugQuery,
  PartnerBySlugResponse,
} from "sdk/partners";
import {
  PasswordsUpdateParams,
  passwordsUpdateQuery,
  PasswordsCreateParams,
  passwordsCreateQuery,
} from "sdk/passwords";
import {
  ProfileDetailsUpdateParams,
  profileDetailsUpdateQuery,
} from "sdk/profileDetails";
import {
  profilePictureDestroyQuery,
  ProfilePictureCreateParams,
  profilePictureCreateQuery,
} from "sdk/profilePicture";
import { programIndexQuery, ProgramIndexResponse } from "sdk/program";
import {
  PromoCodesShowParams,
  promoCodesShowQuery,
  PromoCodesShowResponse,
} from "sdk/promoCodes";
import {
  RecommendationsCreateParams,
  recommendationsCreateQuery,
  RecommendationsIndexParams,
  recommendationsIndexQuery,
  RecommendationsIndexResponse,
  RecommendationsUpdateParams,
  recommendationsUpdateQuery,
} from "sdk/recommendations";
import {
  RegistrationsCreateParams,
  registrationsCreateQuery,
  RegistrationsCreateResponse,
  RegistrationsOnboardingCreateParams,
  registrationsOnboardingCreateQuery,
  RegistrationsOnboardingCreateResponse,
} from "sdk/registrations";
import {
  SessionsCreateParams,
  sessionsCreateQuery,
  SessionsCreateResponse,
  sessionsDestroyQuery,
  diagnosticSessionsDestroyQuery,
  impersonationSessionsDestroyQuery,
} from "sdk/sessions";
import {
  setupIntentsIndexQuery,
  SetupIntentsIndexResponse,
} from "sdk/setupIntents";
import {
  SubscriptionsCreateParams,
  subscriptionsCreateQuery,
  SubscriptionsCreateResponse,
  subscriptionsDestroyQuery,
  subscriptionsPauseQuery,
  subscriptionsResumeQuery,
  SubscriptionsUpdateParams,
  subscriptionsUpdateQuery,
} from "sdk/subscriptions";
import {
  topicsShowQuery,
  Topic,
  TopicsShowParams,
  TopicsIndexResponse,
  topicsIndexQuery,
} from "sdk/topics";
import {
  TrialOffersUpdateParams,
  trialOffersUpdateQuery,
  TrialOffersUpdateResponse,
} from "sdk/trialOffers";
import { UserUiUpdateParams, userUiUpdateQuery } from "sdk/userUi";
import { signOut, setCsrfToken } from "./authentication";
import { setGitSha } from "./gitSha";
import { RootState } from "./store";

const { VITE_API_URL } = import.meta.env;

// Value is 10 years (in seconds). This is to signify that we do not want to
// revalidate an endpoint, even if unsubscribed from the hook. The RTKQ default
// is 60 seconds.
const MAX_AGE = 60 * 60 * 24 * 365 * 10; // 10 years
interface MetaTag extends Element {
  content: string;
}

const getCsrfTokenFromMetaTag = (): string | undefined => {
  const meta = document.querySelector<MetaTag>("meta[name=csrf-token]");

  return meta?.content;
};

const nonJsonContentTypeEndpoints = ["profilePictureCreate"];

const baseQuery = fetchBaseQuery({
  baseUrl: VITE_API_URL,
  credentials: "include",
  prepareHeaders: (headers, { getState, endpoint }) => {
    const state = getState() as RootState;
    const csrfTokenFromState = state.authentication.csrfToken;
    const token = csrfTokenFromState ?? getCsrfTokenFromMetaTag();

    if (token) {
      headers.set("X-CSRF-Token", token);
    }

    if (!nonJsonContentTypeEndpoints.includes(endpoint)) {
      headers.set("Content-Type", "application/json");
      headers.set("Accept", "application/json");
    }

    const clearCache = window.location.search.includes("clear_cache=1");
    if (clearCache) {
      console.log("clearing cache");
      headers.set("X-CLEAR-CACHE", "1");
    }

    return headers;
  },
});

const baseQueryWithInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    // When the 401 body carries a redirect_to (e.g. a passwordless user steered
    // to /forgot-password), let the caller handle SPA navigation. Dispatching
    // signOut here would force a full-page reload to /login and clobber it.
    const redirectTo = (result.error.data as { redirect_to?: string })
      ?.redirect_to;

    // Don't trigger a global sign-out for diagnostic API endpoints.
    // Diagnostic routes handle 401s gracefully (unauthenticated access is expected
    // for pages like the patient form).
    const url = typeof args === "string" ? args : args.url;
    if (!redirectTo && !url?.startsWith("/api/v1/diagnostics/")) {
      api.dispatch(signOut());
    }
    return result;
  }

  const headers = result.meta?.response?.headers;

  if (!headers) return result;

  const version = headers.get("x-git-sha");
  api.dispatch(setGitSha(version));

  const csrfToken = headers.get("x-csrf-token");
  if (csrfToken) {
    api.dispatch(setCsrfToken(csrfToken));
  }

  return result;
};

export const api = createApi({
  baseQuery: baseQueryWithInterceptor,
  endpoints: (builder) => ({
    articlesIndex: builder.query<ArticlesIndexResponse, ArticlesIndexParams>({
      keepUnusedDataFor: MAX_AGE,
      query: articlesIndexQuery,
    }),
    articlesSearch: builder.query<ArticlesSearchResponse, ArticlesSearchParams>(
      {
        query: articlesSearchQuery,
      },
    ),
    articlesShow: builder.query<Article, ArticlesShowParams>({
      query: articlesShowQuery,
      keepUnusedDataFor: MAX_AGE,
    }),
    authorsShow: builder.query<AuthorsShowResponse, AuthorsShowParams>({
      query: authorsShowQuery,
      keepUnusedDataFor: MAX_AGE,
    }),
    cancellationReasonsCreate: builder.mutation<
      void,
      CancellationReasonsCreateParams
    >({
      query: cancellationReasonsCreateQuery,
    }),
    cohortIndex: builder.query<CohortIndexResponse, void>({
      query: cohortIndexQuery,
      providesTags: ["Cohort"],
    }),
    currentUserIndex: builder.query<CurrentUserIndexResponse, void>({
      query: currentUserIndexQuery,
      providesTags: ["CurrentUser"],
    }),
    currentUserUpdate: builder.mutation<
      CurrentUserUpdateResponse,
      CurrentUserUpdateParams
    >({
      query: currentUserUpdateQuery,
    }),
    diagnosticUserIndex: builder.query<DiagnosticUserIndexResponse, void>({
      query: diagnosticUserIndexQuery,
      providesTags: ["DiagnosticUser"],
    }),
    delayedPasswordCreate: builder.mutation<void, DelayedPasswordCreateParams>({
      query: delayedPasswordCreateQuery,
    }),
    gatheringsDecline: builder.mutation<void, GatheringsDeclineParams>({
      query: gatheringsDeclineQuery,
      invalidatesTags: (_result, _error, { slug }) => [
        { type: "Event", id: "LIST" },
        { type: "Event", id: slug },
      ],
    }),
    gatheringsIndex: builder.query<GatheringsIndexResponse, void>({
      keepUnusedDataFor: MAX_AGE,
      query: gatheringsIndexQuery,
      providesTags: (result) => (result ? [{ type: "Event", id: "LIST" }] : []),
    }),
    gatheringsShow: builder.query<Gathering, GatheringsShowParams>({
      query: gatheringsShowQuery,
      keepUnusedDataFor: MAX_AGE,
      providesTags: (_1, _2, arg) => [{ type: "Event", id: arg.slug }],
    }),
    gatheringsRsvp: builder.mutation<void, GatheringsRsvpParams>({
      query: gatheringsRsvpQuery,
      onQueryStarted: async ({ slug }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          api.util.updateQueryData("gatheringsShow", { slug }, (draft) => {
            draft.rsvped = true;
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }

        dispatch(
          api.util.updateQueryData("gatheringsIndex", undefined, (draft) => {
            const gathering = draft?.find(
              (gathering) => gathering.slug === slug,
            );
            if (gathering) {
              gathering.rsvped = true;
            }
          }),
        );
      },
    }),
    medicalExpertsShow: builder.query<
      MedicalExpertsShowResponse,
      MedicalExpertsShowParams
    >({
      query: medicalExpertsShowQuery,
      keepUnusedDataFor: MAX_AGE,
    }),
    newsletterRegistrationsCreate: builder.mutation<
      void,
      NewsletterRegistrationsCreateParams
    >({
      query: newsletterRegistrationsCreateQuery,
    }),
    onboardingCreate: builder.mutation<
      RegistrationsOnboardingCreateResponse,
      RegistrationsOnboardingCreateParams
    >({
      query: registrationsOnboardingCreateQuery,
    }),
    onboardingIndex: builder.query<OnboardingIndexResponse, void>({
      query: onboardingIndexQuery,
      providesTags: ["Onboarding"],
    }),
    onboardingUpdate: builder.mutation<
      OnboardingUpdateResponse,
      OnboardingUpdateParams
    >({
      query: onboardingUpdateQuery,
    }),
    participationsCreate: builder.mutation<void, ParticipationsCreateParams>({
      query: participationsCreateQuery,
    }),
    partnersIndex: builder.query<PartnerIndexResponse, PartnerIndexParams>({
      query: partnerIndexQuery,
      keepUnusedDataFor: MAX_AGE,
    }),
    partnerBySlug: builder.query<PartnerBySlugResponse, string>({
      // No MAX_AGE: partner metadata (eligibility, support email, logo) is
      // admin-mutable, so use RTK Query's default short cache lifetime.
      query: partnerBySlugQuery,
    }),
    passwordsCreate: builder.mutation<void, PasswordsCreateParams>({
      query: passwordsCreateQuery,
    }),
    passwordsUpdate: builder.mutation<void, PasswordsUpdateParams>({
      query: passwordsUpdateQuery,
    }),
    profileDetailsUpdate: builder.mutation<void, ProfileDetailsUpdateParams>({
      query: profileDetailsUpdateQuery,
      invalidatesTags: ["Cohort"],
    }),
    profilePictureCreate: builder.mutation<void, ProfilePictureCreateParams>({
      query: profilePictureCreateQuery,
      invalidatesTags: ["Cohort"],
    }),
    profilePictureDestroy: builder.mutation<void, void>({
      query: profilePictureDestroyQuery,
      invalidatesTags: ["Cohort"],
    }),
    programIndex: builder.query<ProgramIndexResponse, void>({
      query: programIndexQuery,
    }),
    promoCodesShow: builder.query<PromoCodesShowResponse, PromoCodesShowParams>(
      {
        query: promoCodesShowQuery,
      },
    ),
    recommendationsCreate: builder.mutation<void, RecommendationsCreateParams>({
      query: recommendationsCreateQuery,
      invalidatesTags(_1, _2) {
        const id: RecommendationsIndexParams["type"] = "pending";

        return [{ type: "Recommendation", id }];
      },
    }),
    recommendationsIndex: builder.query<
      RecommendationsIndexResponse,
      RecommendationsIndexParams
    >({
      query: recommendationsIndexQuery,
      providesTags: (_1, _2, arg) => [{ type: "Recommendation", id: arg.type }],
    }),
    recommendationsUpdate: builder.mutation<void, RecommendationsUpdateParams>({
      query: recommendationsUpdateQuery,
      invalidatesTags(_1, _2, arg) {
        const isUndoing = arg.completed === false;

        if (isUndoing) {
          const id1: RecommendationsIndexParams["type"] = "pending";
          const id2: RecommendationsIndexParams["type"] = "determined";

          return [
            { type: "Recommendation", id: id1 },
            { type: "Recommendation", id: id2 },
          ];
        }

        return [];
      },
    }),
    registrationsCreate: builder.mutation<
      RegistrationsCreateResponse,
      RegistrationsCreateParams
    >({
      query: registrationsCreateQuery,
    }),
    sessionsCreate: builder.mutation<
      SessionsCreateResponse,
      SessionsCreateParams
    >({
      query: sessionsCreateQuery,
      invalidatesTags: ["CurrentUser"],
    }),
    sessionsDestroy: builder.mutation<void, void>({
      query: sessionsDestroyQuery,
      invalidatesTags: ["CurrentUser"],
    }),
    diagnosticSessionsDestroy: builder.mutation<void, void>({
      query: diagnosticSessionsDestroyQuery,
      invalidatesTags: ["DiagnosticUser"],
    }),
    impersonationSessionsDestroy: builder.mutation<
      { redirect_to: string },
      void
    >({
      query: impersonationSessionsDestroyQuery,
      invalidatesTags: ["CurrentUser"],
    }),
    setupIntentsIndex: builder.query<SetupIntentsIndexResponse, void>({
      query: setupIntentsIndexQuery,
    }),
    subscriptionsCreate: builder.mutation<
      SubscriptionsCreateResponse,
      SubscriptionsCreateParams
    >({
      query: subscriptionsCreateQuery,
    }),
    billingPortalCreate: builder.mutation<BillingPortalCreateResponse, void>({
      query: billingPortalCreateQuery,
    }),
    subscriptionsDestroy: builder.mutation<void, void>({
      query: subscriptionsDestroyQuery,
    }),
    subscriptionsPause: builder.mutation<void, void>({
      query: subscriptionsPauseQuery,
    }),
    subscriptionsResume: builder.mutation<void, void>({
      query: subscriptionsResumeQuery,
    }),
    subscriptionsUpdate: builder.mutation<void, SubscriptionsUpdateParams>({
      query: subscriptionsUpdateQuery,
    }),
    topicsIndex: builder.query<TopicsIndexResponse, void>({
      keepUnusedDataFor: MAX_AGE,
      query: topicsIndexQuery,
    }),
    topicsShow: builder.query<Topic, TopicsShowParams>({
      keepUnusedDataFor: MAX_AGE,
      query: topicsShowQuery,
    }),
    trialOffersUpdate: builder.mutation<
      TrialOffersUpdateResponse,
      TrialOffersUpdateParams
    >({
      query: trialOffersUpdateQuery,
    }),
    userUiUpdate: builder.mutation<void, UserUiUpdateParams>({
      query: userUiUpdateQuery,
    }),
    diagnosticCheckoutSessionsCreate: builder.mutation<
      DiagnosticCheckoutSessionCreateResponse,
      DiagnosticCheckoutSessionCreateParams
    >({
      query: diagnosticCheckoutSessionsCreateQuery,
    }),
    consultationsCreate: builder.mutation<
      CreateConsultationResponse,
      CreateConsultationRequest
    >({
      query: consultationsCreateQuery,
      invalidatesTags: ["Consultations"],
    }),
    facilitiesSearch: builder.query<
      FacilitiesSearchResponse,
      FacilitiesSearchParams
    >({
      query: facilitiesSearchQuery,
    }),
    featuresShow: builder.query<FeaturesShowResponse, FeaturesShowParams>({
      query: featuresShowQuery,
    }),
    diagnosticUserProductsIndex: builder.query<
      DiagnosticUserProductsIndexResponse,
      void
    >({
      query: diagnosticUserProductsIndexQuery,
      providesTags: ["DiagnosticUserProducts"],
    }),
    diagnosticUserProductShow: builder.query<
      DiagnosticUserProductShowResponse,
      string | number
    >({
      query: diagnosticUserProductShowQuery,
      providesTags: ["DiagnosticUserProducts"],
    }),
  }),
  // This is the default value. Added for clarity.
  reducerPath: "api",
  tagTypes: [
    "Articles",
    "Cohort",
    "Consultations",
    "CurrentUser",
    "DiagnosticUser",
    "DiagnosticUserProducts",
    "Event",
    "Onboarding",
    "Recommendation",
  ],
});

export const {
  useArticlesIndexQuery,
  useArticlesShowQuery,
  useAuthorsShowQuery,
  useBillingPortalCreateMutation,
  useCohortIndexQuery,
  useCurrentUserUpdateMutation,
  useCancellationReasonsCreateMutation,
  useDelayedPasswordCreateMutation,
  useGatheringsIndexQuery,
  useGatheringsShowQuery,
  useGatheringsRsvpMutation,
  useGatheringsDeclineMutation,
  useLazyArticlesSearchQuery,
  useLazyCurrentUserIndexQuery,
  useLazyDiagnosticUserIndexQuery,
  useLazyPromoCodesShowQuery,
  useLazySetupIntentsIndexQuery,
  useLazyOnboardingIndexQuery,
  useMedicalExpertsShowQuery,
  useNewsletterRegistrationsCreateMutation,
  useOnboardingCreateMutation,
  useOnboardingUpdateMutation,
  useParticipationsCreateMutation,
  usePartnersIndexQuery,
  usePartnerBySlugQuery,
  usePasswordsCreateMutation,
  usePasswordsUpdateMutation,
  usePrefetch,
  useProfileDetailsUpdateMutation,
  useProfilePictureCreateMutation,
  useProfilePictureDestroyMutation,
  useProgramIndexQuery,
  useRecommendationsCreateMutation,
  useRecommendationsIndexQuery,
  useRecommendationsUpdateMutation,
  useRegistrationsCreateMutation,
  useSessionsCreateMutation,
  useSessionsDestroyMutation,
  useDiagnosticSessionsDestroyMutation,
  useImpersonationSessionsDestroyMutation,
  useSubscriptionsCreateMutation,
  useSubscriptionsDestroyMutation,
  useSubscriptionsPauseMutation,
  useSubscriptionsResumeMutation,
  useSubscriptionsUpdateMutation,
  useTopicsIndexQuery,
  useTopicsShowQuery,
  useTrialOffersUpdateMutation,
  useUserUiUpdateMutation,
  useDiagnosticCheckoutSessionsCreateMutation,
  useConsultationsCreateMutation,
  useLazyFacilitiesSearchQuery,
  useFeaturesShowQuery,
  useDiagnosticUserProductsIndexQuery,
  useDiagnosticUserProductShowQuery,
  util,
} = api;
